import { isBefore } from 'date-fns';
import React, { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import * as Yup from 'yup';

import { Client, GUID, MembershipType, PersonOrgType } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { getClientTypeAlias, getPersonOrgName, parseLocalDate, useRequiredParams } from 'utils';
import { useCommandCenterHouseholdProfile } from 'views/household/household-queries';
import { ClientFormValues } from 'views/person/person-form-model';
import { FormRadioButtons, FormSection, FormSelect, requiredStringValidator, VerticalLayout } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { useClient, useClientProfilesForHousehold, useSaveClient } from './client-queries';
import { JointFormFields } from './JointFormFields';

const formSchema: Yup.ObjectSchema<ClientFormValues> = Yup.object({
    personOrgType: Yup.mixed<PersonOrgType>()
        .oneOf(Object.values(PersonOrgType), 'Account owner type is required')
        .required(),
    personOrgGUID: requiredStringValidator.label('Owner person').when('personOrgType', {
        is: PersonOrgType.JOINT_PERSONS,
        then: (schema) => schema.notRequired(),
    }),
    startDate: requiredStringValidator,
    endDate: Yup.string().test(
        'end-date-not-before-start-date',
        'End date cannot be before the start date',
        (endDate, context) => !(endDate && isBefore(parseLocalDate(endDate), parseLocalDate(context.parent.startDate))),
    ),
    orgName: Yup.string().when('personOrgType', {
        is: PersonOrgType.JOINT_PERSONS,
        then: () => requiredStringValidator.label('Joint name'),
    }),
    highNetWorth: Yup.boolean(),
    jointOwners: Yup.object({
        [MembershipType.JOINT_PRIMARY]: requiredStringValidator.label('Primary owner'),
        [MembershipType.JOINT_SECONDARY]: requiredStringValidator
            .label('Secondary owner')
            .test(
                'secondary-different-from-primary',
                'The secondary owner cannot be the same as the primary owner',
                (jointSecondary, context) => jointSecondary !== context.parent[MembershipType.JOINT_PRIMARY],
            ),
    }).default(undefined),
});

export const ClientDrawer = ({ onClose, clientGUID }: { onClose: () => void; clientGUID?: GUID<Client> }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    const { data: existingClient, isLoading } = useClient(clientGUID);
    const { mutate: saveClient, isPending: saveInProgress } = useSaveClient(globalAccountGUID, clientGUID);

    const defaultValues = useMemo(() => {
        if (!existingClient) {
            return {};
        }

        return {
            personOrgGUID: existingClient.client.personOrgGUID,
            personOrgType: existingClient.clientPersonOrgProfile.personOrg.personOrgType,
            startDate: existingClient.client.startDate,
            endDate: existingClient.client.endDate,
            orgName: existingClient.clientPersonOrgProfile.personOrg.orgName?.plainText,
            highNetWorth: existingClient.clientPersonOrgProfile.financialInformation?.highNetWorth,
            jointOwners:
                existingClient.clientPersonOrgProfile.personOrg.personOrgType === PersonOrgType.JOINT_PERSONS
                    ? {
                          [MembershipType.JOINT_PRIMARY]: existingClient.memberProfiles.find(
                              (memberProfile) => memberProfile.link.membershipType === MembershipType.JOINT_PRIMARY,
                          )?.profile.personOrgGUID,
                          [MembershipType.JOINT_SECONDARY]: existingClient.memberProfiles.find(
                              (memberProfile) => memberProfile.link.membershipType === MembershipType.JOINT_SECONDARY,
                          )?.profile.personOrgGUID,
                      }
                    : undefined,
        };
    }, [existingClient]);

    const onSubmit = (data: ClientFormValues, close: () => void) => saveClient(data, { onSuccess: close });

    return (
        <FormSheet
            title={clientGUID ? 'Edit Account Owner' : 'New Account Owner'}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={formSchema}
            defaultValues={defaultValues}
            isLoading={!!clientGUID && isLoading}
            pending={saveInProgress}
            labelWidth="220px"
            submitLabel={clientGUID ? 'Save' : 'Create'}
        >
            <FormFields clientGUID={clientGUID} />
        </FormSheet>
    );
};

const FormFields = ({ clientGUID }: { clientGUID?: GUID<Client> }) => {
    const { reset } = useFormContext();
    const [personOrgType] = useWatch({ name: ['personOrgType'] });
    const isEditing = !!clientGUID;

    useEffect(() => {
        !isEditing && reset({ personOrgType });
    }, [isEditing, personOrgType, reset]);

    return (
        <VerticalLayout $gap={24}>
            <FormSection title="Account Owner Type">
                <FormSelect name="personOrgType" label="Select type" disabled={isEditing}>
                    <option></option>
                    <option value={PersonOrgType.PERSON}>Individual</option>
                    <option value={PersonOrgType.JOINT_PERSONS}>Joint</option>
                    <option value={PersonOrgType.TRUST}>Trust</option>
                    <option value={PersonOrgType.ORGANIZATION}>Entity</option>
                </FormSelect>
            </FormSection>

            {personOrgType && (
                <>
                    <FormSection title="Key Dates" hasDivider>
                        <FormDateInput name="startDate" label="Client agreement start date" />
                        {isEditing && <FormDateInput name="endDate" label="Client agreement end date" />}
                    </FormSection>

                    {personOrgType === PersonOrgType.JOINT_PERSONS ? (
                        <JointFormFields isEditing={isEditing} />
                    ) : (
                        <PersonOrgList personOrgType={personOrgType} disabled={isEditing} />
                    )}
                </>
            )}
        </VerticalLayout>
    );
};

const PersonOrgList = ({ personOrgType, disabled }: { personOrgType: PersonOrgType; disabled: boolean }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const personOrgGUID = useWatch({ name: 'personOrgGUID' });

    const { data: clientProfiles } = useClientProfilesForHousehold(globalAccountGUID);
    const { data: accountProfile } = useCommandCenterHouseholdProfile(globalAccountGUID);

    const values = useMemo(() => {
        if (!accountProfile) {
            return [];
        }

        if (disabled) {
            const selected = accountProfile?.personOrgFinancialProfiles.find(
                ({ profile }) => profile.personOrgGUID === personOrgGUID,
            );
            return [
                {
                    value: selected?.profile.personOrgGUID || '',
                    label: getPersonOrgName(selected?.profile.personOrg),
                },
            ];
        }

        const takenPersonOrgs = clientProfiles?.map((profile) => profile.clientPersonOrgProfile.personOrgGUID);
        return accountProfile?.personOrgFinancialProfiles
            ?.filter(
                ({ profile }) =>
                    profile.personOrg.personOrgType === personOrgType &&
                    !takenPersonOrgs?.includes(profile.personOrgGUID),
            )
            .map(({ profile }) => ({
                value: profile.personOrgGUID,
                label: getPersonOrgName(profile.personOrg),
            }));
    }, [accountProfile, clientProfiles, disabled, personOrgGUID, personOrgType]);

    return (
        <FormSection hasDivider disabled={disabled}>
            <FormRadioButtons
                required
                name="personOrgGUID"
                title={`Select Owner ${getClientTypeAlias(personOrgType)}`}
                values={values}
                placeholder="There is either no entry of the selected type or all of them are already set as Account Owners."
            />
        </FormSection>
    );
};
